import {
  CalendarToday as CalendarTodayIcon,
  InfoOutlined as InfoOutlinedIcon,
  MoreVert as MoreVertIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { IconButton, InputLabel, TextField } from '@mui/material';
import { trim } from 'lodash';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { SimpleDialog } from 'src/components/modal';
import { PATHS } from 'src/constants/paths';
import { ILocationGroupTabList } from 'src/interfaces';
import { lang } from 'src/utils';

import { ActionsContainer } from '../../../LocationListPage.styled';
import { LocationGroupMenu } from '../LocationGroupMenu/LocationGroupMenu';
import { LocationGroupActionButtons } from './LocationGroupActionButtons';
import { useLocationGroupRowActions } from './useLocationGroupRowActions';

export const LocationGroupRowActions = ({
  row,
}: {
  row: ILocationGroupTabList;
}) => {
  const navigate = useNavigate();
  const {
    menu,
    deleteModal,
    duplicateModal,
    isDeleting,
    isDuplicating,
    duplicatedName,
    onMenuClick,
    onDuplicateClick,
    onDuplicateChange,
    onCloseDuplicateModal,
    onConfirmDuplicate,
    onDeleteClick,
    onConfirmDelete,
  } = useLocationGroupRowActions(row);

  const actionItems = useMemo(
    () => [
      {
        title: lang.locationGroupDetails,
        ariaLabel: 'clock',
        icon: <InfoOutlinedIcon />,
        onClick: () =>
          navigate(PATHS.LOCATION_GROUP_DETAILS(row.id.toString())),
      },
      {
        title: lang.locationGroupSchedule,
        ariaLabel: 'calendar',
        icon: <CalendarTodayIcon />,
        onClick: () =>
          navigate(PATHS.LOCATION_GROUP_CALENDAR(row.id.toString()), {
            state: row.name,
          }),
      },
      {
        title: lang.locationGroupTimings,
        ariaLabel: 'clock',
        icon: <ScheduleIcon />,
        onClick: () =>
          navigate(PATHS.LOCATION_GROUP_TIMINGS(row.id.toString())),
      },
    ],
    [navigate, row.id, row.name],
  );

  return (
    <ActionsContainer>
      <LocationGroupActionButtons items={actionItems} />
      <IconButton
        aria-label="more"
        data-testid="row-menu-icon"
        onClick={onMenuClick}>
        <MoreVertIcon />
      </IconButton>
      <LocationGroupMenu
        anchorEl={menu.anchorEl}
        onClose={menu.onCloseMenu}
        row={row}
        onDuplicateClick={onDuplicateClick}
        onDeleteClick={onDeleteClick}
      />
      <SimpleDialog
        actionColor="error"
        open={deleteModal.open}
        cancelText={lang.cancel}
        confirmText={lang.deleteText}
        title={lang.locationGroupDeleteModal.title}
        subTitle={lang.deleteModalSubtitle}
        onClose={deleteModal.onCloseModal}
        onClickCancel={deleteModal.onCloseModal}
        modalSize="sm"
        disabled={isDeleting}
        onClickConfirm={onConfirmDelete}
      />
      {duplicateModal.open && (
        <SimpleDialog
          title={lang.duplicateLocationGroupModal.title}
          open={duplicateModal.open}
          confirmText={lang.create}
          isLoading={isDuplicating}
          disabled={!trim(duplicatedName) || isDuplicating}
          onClose={onCloseDuplicateModal}
          onClickCancel={onCloseDuplicateModal}
          onClickConfirm={onConfirmDuplicate}
          modalSize="sm">
          <>
            <InputLabel>
              {lang.duplicateLocationGroupModal.inputLabel}
            </InputLabel>
            <TextField
              fullWidth
              onChange={onDuplicateChange}
              data-testid="duplicate-location-group-name-input"
            />
          </>
        </SimpleDialog>
      )}
    </ActionsContainer>
  );
};
